import { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const EMOJI_SUGGESTIONS = [
    "🎬", "💾", "📷", "📚", "🎮", "💻", "📡", "📊", "🤖", "⚙️",
    "⬇️", "🏠", "🧰", "📁", "🔐", "☁️", "🎵", "📝", "🗄️", "🔎",
];

const PRESET_COLORS = [
    "#e11d48", "#f97316", "#eab308", "#22c55e", "#14b8a6",
    "#0ea5e9", "#6366f1", "#a855f7", "#ec4899", "#64748b",
];

export default function CategoriesPage() {
    const [categories, setCategories] = useState([]);
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);

    const refresh = async () => {
        setLoading(true);
        try {
            const [c, a] = await Promise.all([api.get("/categories"), api.get("/apps")]);
            setCategories(c.data);
            setApps(a.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const countsByCategory = useMemo(() => {
        return apps.reduce((acc, a) => {
            if (a.category_id) acc[a.category_id] = (acc[a.category_id] || 0) + 1;
            return acc;
        }, {});
    }, [apps]);

    const openCreate = () => {
        setEditing({ id: null, name: "", icon: "📁", color: PRESET_COLORS[0] });
        setOpen(true);
    };

    const openEdit = (c) => {
        setEditing({ id: c.id, name: c.name, icon: c.icon, color: c.color || PRESET_COLORS[0] });
        setOpen(true);
    };

    const save = async () => {
        const name = editing.name.trim();
        if (!name) {
            toast.error("Category name is required");
            return;
        }
        setSaving(true);
        try {
            const body = { name, icon: editing.icon || "📁", color: editing.color };
            if (editing.id) {
                await api.put(`/categories/${editing.id}`, body);
                toast.success("Category updated");
            } else {
                await api.post("/categories", body);
                toast.success("Category created");
            }
            setOpen(false);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!pendingDelete) return;
        try {
            const { data } = await api.delete(`/categories/${pendingDelete.id}`);
            const moved = data?.apps_uncategorized || 0;
            toast.success(
                moved > 0
                    ? `Category deleted · ${moved} app${moved === 1 ? "" : "s"} moved to Uncategorized`
                    : "Category deleted",
            );
            setPendingDelete(null);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const move = async (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= categories.length) return;
        const next = categories.slice();
        [next[index], next[target]] = [next[target], next[index]];
        setCategories(next); // optimistic
        try {
            await api.put("/categories/reorder", { ids: next.map((c) => c.id) });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
            refresh();
        }
    };

    return (
        <>
            <Header />
            <main className="max-w-[1100px] mx-auto px-6 sm:px-10 py-10 sm:py-12" data-testid="categories-page">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                    <div>
                        <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground font-semibold mb-3">
                            Administration
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold">
                            Categories
                        </h1>
                        <p className="text-muted-foreground mt-2 max-w-xl">
                            Organise the dashboard sidebar. Assign apps to a category from the edit
                            button on each tile.
                        </p>
                    </div>
                    <Button onClick={openCreate} className="rounded-full h-11 px-5" data-testid="add-category-button">
                        <Plus className="h-4 w-4 mr-1.5" /> New category
                    </Button>
                </div>

                {loading ? (
                    <div className="text-muted-foreground">Loading categories...</div>
                ) : categories.length === 0 ? (
                    <div className="glass-tile rounded-3xl p-12 text-center">
                        <h3 className="font-display text-2xl font-semibold mb-2">No categories</h3>
                        <p className="text-muted-foreground mb-6">
                            Every app will show under "Uncategorized" until you add one.
                        </p>
                        <Button onClick={openCreate} data-testid="empty-add-category">
                            <Plus className="h-4 w-4 mr-1.5" /> Add a category
                        </Button>
                    </div>
                ) : (
                    <div className="glass-tile rounded-3xl divide-y divide-border overflow-hidden">
                        {categories.map((c, i) => (
                            <div
                                key={c.id}
                                className="flex items-center gap-4 px-5 py-4"
                                data-testid={`category-row-${c.id}`}
                            >
                                <span className="text-2xl leading-none w-8 text-center">{c.icon}</span>
                                <div className="min-w-0 flex-1">
                                    <p className="font-medium truncate" style={c.color ? { color: c.color } : undefined}>
                                        {c.name}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {countsByCategory[c.id] || 0} app
                                        {(countsByCategory[c.id] || 0) === 1 ? "" : "s"}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8 rounded-full"
                                        disabled={i === 0}
                                        onClick={() => move(i, -1)}
                                        aria-label={`Move ${c.name} up`}
                                        data-testid={`category-up-${c.id}`}
                                    >
                                        <ChevronUp className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8 rounded-full"
                                        disabled={i === categories.length - 1}
                                        onClick={() => move(i, 1)}
                                        aria-label={`Move ${c.name} down`}
                                        data-testid={`category-down-${c.id}`}
                                    >
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8 rounded-full"
                                        onClick={() => openEdit(c)}
                                        aria-label={`Edit ${c.name}`}
                                        data-testid={`edit-category-${c.id}`}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8 rounded-full text-red-500 hover:text-red-500"
                                        onClick={() => setPendingDelete(c)}
                                        aria-label={`Delete ${c.name}`}
                                        data-testid={`delete-category-${c.id}`}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-[480px]" data-testid="category-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display tracking-tight">
                            {editing?.id ? "Edit category" : "New category"}
                        </DialogTitle>
                        <DialogDescription>
                            Pick an icon and a name. Apps are assigned from their own edit dialog.
                        </DialogDescription>
                    </DialogHeader>
                    {editing && (
                        <div className="grid gap-4 py-2">
                            <div className="grid gap-1.5">
                                <Label htmlFor="category-name">Name</Label>
                                <Input
                                    id="category-name"
                                    value={editing.name}
                                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                    placeholder="Media"
                                    data-testid="input-category-name"
                                />
                            </div>
                            <div className="grid gap-1.5">
                                <Label htmlFor="category-icon">Icon</Label>
                                <Input
                                    id="category-icon"
                                    value={editing.icon}
                                    onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                                    placeholder="🎬"
                                    className="text-xl"
                                    data-testid="input-category-icon"
                                />
                                <div className="flex flex-wrap gap-1.5 pt-1">
                                    {EMOJI_SUGGESTIONS.map((emoji) => (
                                        <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => setEditing({ ...editing, icon: emoji })}
                                            className={`h-9 w-9 rounded-lg text-lg transition-colors ${
                                                editing.icon === emoji
                                                    ? "bg-foreground/10 ring-1 ring-foreground/20"
                                                    : "hover:bg-foreground/5"
                                            }`}
                                            aria-label={`Use ${emoji}`}
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="grid gap-1.5">
                                <Label>Colour</Label>
                                <div className="flex flex-wrap gap-2">
                                    {PRESET_COLORS.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() => setEditing({ ...editing, color })}
                                            className={`h-7 w-7 rounded-full transition-transform ${
                                                editing.color === color
                                                    ? "ring-2 ring-offset-2 ring-foreground/40 scale-110"
                                                    : ""
                                            }`}
                                            style={{ backgroundColor: color }}
                                            aria-label={`Use colour ${color}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)} data-testid="cancel-category-dialog">
                            Cancel
                        </Button>
                        <Button onClick={save} disabled={saving} data-testid="save-category-dialog">
                            {saving ? "Saving..." : editing?.id ? "Save changes" : "Create category"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete "{pendingDelete?.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {(countsByCategory[pendingDelete?.id] || 0) > 0
                                ? `${countsByCategory[pendingDelete.id]} app${
                                      countsByCategory[pendingDelete.id] === 1 ? "" : "s"
                                  } will move to Uncategorized. No apps are deleted.`
                                : "This category is empty. No apps are affected."}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cancel-delete-category">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={remove}
                            className="bg-red-500 hover:bg-red-600"
                            data-testid="confirm-delete-category"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
